export type ListStatus = 'ready' | 'in_progress' | 'complete';

export type ListPriority = number; // 1-99999 where 1 is highest priority

export interface ListItem {
  id: string;
  content: string;
  // REMOVED: completed: boolean;
  // REMOVED: completedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  priority: number;
  
  // Enhanced features
  description?: string; // Optional extended description
  dueDate?: Date; // Item-level deadlines
  estimatedMinutes?: number; // Time estimation for the item
  actualMinutes?: number; // Actual time spent
  
  // Context and categorization
  category?: string; // Custom categorization
  difficulty?: 'easy' | 'medium' | 'hard'; // Complexity level
  
  // History and tracking
  history?: ItemHistoryEntry[]; // Track changes over time
  notes?: string; // Additional notes or context
  
  // Dependencies and relationships
  dependsOn?: string[]; // IDs of other items this depends on
  blockedBy?: string[]; // IDs of items blocking this one
  
  // Metadata
  attachments?: ItemAttachment[]; // File or link attachments
  reminders?: ItemReminder[]; // Reminder settings
}

export interface ItemHistoryEntry {
  id: string;
  action: 'created' | 'updated' | 'completed' | 'uncompleted' | 'moved' | 'deleted';
  timestamp: Date;
  userId?: string; // For multi-user scenarios
  changes?: Record<string, { from: any; to: any }>; // What changed
  note?: string; // Optional note about the change
}

export interface ItemAttachment {
  id: string;
  name: string;
  type: 'file' | 'link' | 'image';
  url: string;
  size?: number; // File size in bytes
  addedAt: Date;
}

export interface ItemReminder {
  id: string;
  type: 'absolute' | 'relative';
  time?: Date; // For absolute reminders
  minutesBefore?: number; // For relative reminders (before due date)
  message?: string;
  isActive: boolean;
}

// Bulk operations for items
export interface ItemBulkOperation {
  type: 'complete' | 'uncomplete' | 'delete' | 'move' | 'priority_update';
  itemIds: string[];
  payload?: any;
}

export interface List {
  id: string;
  title: string;
  description: string;
  status: ListStatus;
  priority: ListPriority;
  
  // REMOVED: progress: number; // 0-100 based on completed items
  // REMOVED: totalItems: number;
  // REMOVED: completedItems: number;
  
  // Relationships
  goalId?: string; // Optional link to a goal
  templateId?: string; // If cloned from template, reference to original
  
  // Items within the list
  items: ListItem[];
  
  // Dates
  deadline?: Date;
  startDate?: Date;
  completedDate?: Date;
  createdAt: Date;
  updatedAt: Date;

  // Metadata
  color?: string; // For visual organization
  estimatedHours?: number;
  actualHours?: number;
}

export interface ListFormData {
  title: string;
  description: string;
  status: ListStatus;
  priority: ListPriority;
  goalId?: string;
  deadline?: Date;
  startDate?: Date;
  color?: string;
  // Items are optional - users will add them on the list-items page
  items?: Omit<ListItem, 'id' | 'createdAt' | 'updatedAt' | 'priority'>[];
}

export interface ListFilters {
  status?: ListStatus[];
  goalId?: string[];
  aspirationId?: string[];
  search?: string;
  hasDeadline?: boolean;
  isOverdue?: boolean;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

export interface ListSort {
  field: 'title' | 'priority' | 'progress' | 'deadline' | 'updatedAt' | 'status' | 'createdAt';
  direction: 'asc' | 'desc';
}

export interface ListTemplate {
  id: string;
  title: string;
  description: string;
  category: string;
  items: Omit<ListItem, 'id' | 'createdAt' | 'priority'>[];
  estimatedHours?: number;
  color?: string;
  usageCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface ListStats {
  total: number;
  byStatus: {
    ready: number;
    in_progress: number;
    complete: number;
  };
  byProgress: {
    notStarted: number;
    inProgress: number;
    completed: number;
  };
  averageCompletionTime: number; // in days
  totalItems: number;
  completedItems: number;
  overdue: number;
}

export interface BulkOperation {
  type: 'status_change' | 'goal_link' | 'archive' | 'delete';
  listIds: string[];
  payload?: any;
}

// Item-specific interfaces
export interface ItemFilters {
  completed?: boolean;
  category?: string;
  difficulty?: ('easy' | 'medium' | 'hard')[];
  search?: string;
  hasDeadline?: boolean;
  isOverdue?: boolean;
  hasDependencies?: boolean;
  isBlocked?: boolean;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

export interface ItemSort {
  field: 'content' | 'priority' | 'createdAt' | 'updatedAt' | 'dueDate' | 'difficulty';
  direction: 'asc' | 'desc';
}

// Undo functionality
export interface UndoAction {
  id: string;
  type: 'item_complete' | 'item_delete' | 'item_move' | 'bulk_operation';
  timestamp: Date;
  listId: string;
  description: string; // Human-readable description
  data: any; // Data needed to undo the action
  canUndo: boolean;
}

export interface UndoStack {
  actions: UndoAction[];
  maxSize: number;
  currentIndex: number;
}

export const LIST_STATUS_CONFIG = {
  ready: {
    label: 'Ready',
    color: 'green',
    badge: 'bg-green-100 text-green-800 border-green-200',
    description: 'Prepared but not started'
  },
  in_progress: {
    label: 'In Progress',
    color: 'orange',
    badge: 'bg-orange-100 text-orange-800 border-orange-200',
    description: 'Actively being worked on'
  },
  complete: {
    label: 'Complete',
    color: 'gray',
    badge: 'bg-gray-100 text-gray-800 border-gray-200',
    description: 'Finished and archived'
  }
} as const;

export const TEMPLATE_CATEGORIES = [
  'Daily Routines',
  'Work Projects', 
  'Health & Fitness',
  'Learning & Education',
  'Home & Personal',
  'Travel & Events',
  'Financial Planning',
  'Relationships',
  'Creative Projects',
  'Business Development'
] as const;

export type TemplateCategory = typeof TEMPLATE_CATEGORIES[number];

// Universal Date-Based Completion System Types
export interface DateScopedCompletion {
  listId: string;
  contextDate: string; // YYYY-MM-DD format  
  completedItemIds: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface DateScopedListData {
  listId: string;
  contextDate: string;
  baseList: List;
  items: (ListItem & { completed: boolean; completedAt?: Date })[];
  progress: number;
  completedItems: number;
  totalItems: number;
  status: ListStatus;
  isCompletelyFinished: boolean;
}

// Optimized Storage Types (v2) - for reduced localStorage footprint
export interface OptimizedCompletionStorage {
  v: 2; // version
  archiveThresholdDays: number;
  lastArchiveDate: string | null;
  l: {  // lists (short key for storage efficiency)
    [listId: string]: {
      d: { [date: string]: string[] }; // date -> completed item IDs
      u: string; // lastUpdated ISO string
    };
  };
}

export interface CompletionArchive {
  v: 1;
  archivedAt: string;
  dateRange: { start: string; end: string };
  data: {
    [listId: string]: {
      d: { [date: string]: string[] };
      u: string;
    };
  };
}

export interface CompletionArchiveIndex {
  archives: Array<{
    filename: string;
    dateRange: { start: string; end: string };
    entriesCount: number;
    archivedAt: string;
  }>;
}

export interface CompletionStorageStats {
  totalEntries: number;
  totalBytes: number;
  entriesByList: Record<string, number>;
  oldestEntry: string | null;
  newestEntry: string | null;
  archivableCount: number;
  archivedFilesCount: number;
}